import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useUserStore } from '@/state/userStore';

type RatingPromptTrigger =
  | 'trip_creation_success'
  | 'email_sync_import_success'
  | 'trip_completed_milestone'
  | 'yearly_summary_share_success'
  | 'template_rating_success'
  | 'trip_recap_share_success'
  | 'trip_dossier_export_success';

export type AnalyticsEventPayloadMap = {
  app_open: {
    source: 'cold_start' | 'foreground';
    route?: string;
    daysSinceSignup?: number;
    daysSinceLastActive?: number;
    hasCompletedOnboarding: boolean;
    hasTrips?: boolean;
  };
  session_start: {
    source: 'cold_start' | 'foreground';
    route?: string;
    daysSinceSignup?: number;
    daysSinceLastActive?: number;
    hasCompletedOnboarding: boolean;
    hasTrips?: boolean;
  };
  signup_completed: {
    method: 'email' | 'google' | 'apple' | 'unknown';
    isNewUser: boolean;
  };
  onboarding_step_viewed: {
    step: string;
    stepIndex?: number;
    totalSteps?: number;
    entryPoint?: 'standard' | 'template_deeplink' | 'invite_deeplink' | 'unknown';
  };
  onboarding_step_completed: {
    step: string;
    stepIndex?: number;
    totalSteps?: number;
    entryPoint?: 'standard' | 'template_deeplink' | 'invite_deeplink' | 'unknown';
  };
  onboarding_step_skipped: {
    step: string;
    stepIndex?: number;
    totalSteps?: number;
    reason?: 'user_skip' | 'already_completed' | 'not_required';
  };
  trip_completed_view: {
    tripId: string;
    entryPoint: 'default' | 'history' | 'deeplink' | 'rail';
    source?: string;
    viewDurationMs?: number;
  };
  trip_dossier_export: {
    tripId: string;
    channel: 'pdf' | 'csv' | 'api' | 'email';
    role?: string;
    status: 'started' | 'completed' | 'failed';
  };
  trip_rebook_initiated: {
    tripId: string;
    template?: string;
    destination?: string;
  };
  trip_memory_added: {
    tripId: string;
    hasPhoto: boolean;
  };
  trip_memory_shared: {
    tripId: string;
    channel: 'share-sheet' | 'pdf' | 'deeplink';
  };
  trip_export_audit: {
    tripId: string;
    actor: string;
    medium: 'client' | 'server';
  };
  completed_trip_push_sent: {
    tripId: string;
    cadence: 'same_day' | 't_plus_3' | 't_plus_14';
    channel: 'push' | 'email';
  };
  completed_trip_push_opened: {
    tripId: string;
    cadence: 'same_day' | 't_plus_3' | 't_plus_14';
    channel: 'push' | 'email';
  };
  day_briefing_view: {
    tripId: string;
    surface: 'active_trip_card' | 'bottom_sheet' | 'minimized_tray' | 'notification';
    itemCount: number;
    hasWeather: boolean;
    tripDay?: number | null;
  };
  day_briefing_expand: {
    tripId: string;
    surface: 'minimized_tray' | 'bottom_sheet' | 'active_trip_card';
  };
  day_briefing_dismiss: {
    tripId: string;
    surface: 'active_trip_card' | 'bottom_sheet' | 'minimized_tray';
  };
  day_briefing_action_tap: {
    tripId: string;
    itemKind: 'weather' | 'booking' | 'checklist' | 'budget' | 'safety' | 'itinerary';
    itemId: string;
    priority: 'high' | 'medium' | 'low';
    surface: 'active_trip_card' | 'bottom_sheet' | 'minimized_tray' | 'notification';
  };
  day_briefing_notification_open: {
    tripId: string;
    notificationId?: string;
  };
  // Template-related events
  template_browser_opened: {
    source: 'empty_state' | 'quick_widget' | 'manual_form' | 'ai_assistant' | 'deeplink' | 'profile' | 'unknown';
    topicTag?: string;
    templateId?: string;
  };
  template_viewed: {
    templateId: string;
    templateTitle: string;
    destination: string;
    duration: number;
    source: 'browser' | 'quick_widget' | 'search' | 'deeplink';
    topicTag?: string;
  };
  template_used: {
    templateId: string;
    tripId: string;
    hasCustomName: boolean;
    hasCustomBudget: boolean;
    startDate: string;
    source?: 'browser' | 'quick_widget' | 'search' | 'deeplink' | 'unknown';
    topicTag?: string;
  };
  template_customization_started: {
    templateId: string;
    templateTitle: string;
    source?: 'browser' | 'quick_widget' | 'search' | 'deeplink' | 'unknown';
    topicTag?: string;
  };
  template_customization_completed: {
    templateId: string;
    tripId: string;
    customizationTimeMs: number;
    source?: 'browser' | 'quick_widget' | 'search' | 'deeplink' | 'unknown';
    topicTag?: string;
  };
  template_search: {
    query: string;
    resultsCount: number;
    topicTag?: string;
  };
  template_filter_applied: {
    difficulty?: string[];
    travelStyle?: string[];
    hasDurationFilter: boolean;
    hasBudgetFilter: boolean;
    hasTopicFilter?: boolean;
    featuredOnly?: boolean;
  };
  template_created: {
    templateId: string;
    creatorId: string;
    destination: string;
    duration: number;
  };
  template_rated: {
    templateId: string;
    tripId: string;
    rating: number;
    hasReview: boolean;
    source: 'trip_card' | 'template_detail' | 'post_completion_prompt' | 'unknown';
  };
  template_rating_prompt_shown: {
    templateId: string;
    tripId: string;
    source: 'trip_card_completed';
  };
  template_rating_prompt_dismissed: {
    templateId: string;
    tripId: string;
    source: 'trip_card_completed';
  };
  template_rating_prompt_completed: {
    templateId: string;
    tripId: string;
    rating: number;
    hasReview: boolean;
    source: 'trip_card_completed';
  };
  template_shared: {
    templateId: string;
    templateTitle: string;
    topicTag?: string;
    source: 'template_card' | 'template_detail' | 'post_use_prompt' | 'unknown';
    platform: string;
  };
  template_deeplink_opened: {
    topicTag?: string;
    templateId?: string;
    ref?: string;
    utmSource?: string;
    authState: 'signed_out' | 'signed_in_not_onboarded' | 'signed_in_onboarded';
    platform: string;
  };
  apple_universal_link_opened: {
    route: '/templates' | '/invite/:linkId' | '/join-collaboration' | '/(tabs)';
    ref?: string;
    utmSource?: string;
    authState: 'signed_out' | 'signed_in_not_onboarded' | 'signed_in_onboarded';
  };
  apple_widget_opt_in_changed: {
    widgetKind: 'next_trip_countdown' | 'smart_day_briefing';
    enabled: boolean;
    source: 'prompt' | 'settings' | 'system';
  };
  apple_spotlight_item_opened: {
    entityType: 'trip' | 'template';
    entityId: string;
    source: 'spotlight' | 'search_suggestion';
  };
  apple_app_intent_invoked: {
    intent:
      | 'open_next_trip'
      | 'show_today_itinerary'
      | 'show_documents'
      | 'open_templates'
      | 'start_navigation'
      | 'check_off_activity';
    result: 'success' | 'failed';
    failureReason?: 'not_authenticated' | 'missing_trip' | 'missing_data' | 'unknown';
  };
  live_activity_gate_evaluated: {
    tripId: string;
    passed: boolean;
    reasons: Array<
      | 'missing_itinerary'
      | 'missing_timezone'
      | 'missing_next_item'
      | 'stale_trip_data'
      | 'feature_disabled'
      | 'ok'
    >;
  };
  // Place rating events
  place_rated: {
    placeId: string;
    rating: number;
    placeName?: string;
    category?: string;
  };
  // Route optimization events
  route_optimized: {
    tripId: string;
    timeSaved: number; // in minutes
    distanceSaved?: number; // in km
    originalRouteDistance?: number;
    optimizedRouteDistance?: number;
  };
  // Collaboration events
  collaborator_added: {
    tripId: string;
    collaboratorId: string;
    collaboratorEmail?: string;
  };
  collaborator_removed: {
    tripId: string;
    collaboratorId: string;
  };
  // Sharing events
  trip_shared: {
    tripId: string;
    platform: 'email' | 'link' | 'social' | 'pdf' | 'other';
    method?: string;
  };
  // Story generation events
  story_generated: {
    tripId: string;
    storyType?: 'timeline' | 'highlights' | 'full';
    hasPhotos: boolean;
  };
  // Analytics view events
  analytics_viewed: {
    tripId?: string;
    viewType: 'trip' | 'user' | 'dashboard';
  };
  // Activation events
  first_trip_created: {
    tripId: string;
    destination?: string;
    method: 'manual' | 'ai' | 'email_sync' | 'template';
  };
  trip_created: {
    tripId: string;
    actorId: string;
    source: 'manual' | 'ai' | 'email_sync' | 'template';
    result: 'success' | 'failed';
    isFirstTrip: boolean;
  };
  invite_sent: {
    tripId: string;
    actorId: string;
    source: 'collaborator_add' | 'pending_invitation' | 'invite_link';
    result: 'success' | 'failed';
    isPending: boolean;
  };
  invite_accepted: {
    tripId: string;
    actorId: string;
    source: 'invite_link' | 'pending_invitation';
    result: 'success' | 'failed';
    linkId?: string;
  };
  collaborator_joined: {
    tripId: string;
    collaboratorId: string;
    source: 'invite_link' | 'pending_invitation';
  };
  email_sync_connect_started: {
    provider: 'gmail' | 'outlook' | 'icloud';
    source: 'onboarding' | 'settings' | 'trips_home' | 'unknown';
  };
  email_sync_connect_completed: {
    provider: 'gmail' | 'outlook' | 'icloud';
    source: 'onboarding' | 'settings' | 'trips_home' | 'unknown';
    result: 'success' | 'failed';
    error?: string;
  };
  email_sync_sync_completed: {
    provider: 'gmail' | 'outlook' | 'icloud';
    source: 'onboarding' | 'settings' | 'trips_home' | 'unknown';
    result: 'success' | 'failed';
    bookingsCount?: number;
    tripsCreated?: number;
    error?: string;
  };
  feedback_entry_tapped: {
    source: 'settings' | 'rating_prompt' | 'positive_moment';
    kind: 'bug' | 'feedback' | 'feature' | 'unhappy_path';
  };
  rating_entry_tapped: {
    source: 'settings' | 'positive_moment';
    path: 'native' | 'store_listing' | 'unavailable';
  };
  expense_settled: {
    tripId: string;
    actorId: string;
    fromUserId: string;
    toUserId: string;
    amount: number;
    currency: string;
    result: 'success' | 'failed';
  };
  // Premium paywall events
  premium_paywall_shown: {
    source:
      | 'feature_gate'
      | 'upgrade_prompt'
      | 'trial_expired'
      | 'other'
      | 'profile_plan_card'
      | 'marketing_upsell'
      | 'itinerary_fill_day'
      | 'itinerary_fill_day_limit'
      | 'itinerary_fill_day_auto_limit';
    feature?: string;
  };
  premium_paywall_started: {
    source:
      | 'feature_gate'
      | 'upgrade_prompt'
      | 'trial_expired'
      | 'other'
      | 'profile_plan_card'
      | 'marketing_upsell'
      | 'itinerary_fill_day'
      | 'itinerary_fill_day_limit'
      | 'itinerary_fill_day_auto_limit';
    plan?: string;
  };
  premium_paywall_converted: {
    plan: string;
    source:
      | 'feature_gate'
      | 'upgrade_prompt'
      | 'trial_expired'
      | 'other'
      | 'profile_plan_card'
      | 'marketing_upsell'
      | 'itinerary_fill_day'
      | 'itinerary_fill_day_limit'
      | 'itinerary_fill_day_auto_limit';
    price?: number;
    currency?: string;
  };
  premium_paywall_failed: {
    source:
      | 'feature_gate'
      | 'upgrade_prompt'
      | 'trial_expired'
      | 'other'
      | 'profile_plan_card'
      | 'marketing_upsell'
      | 'itinerary_fill_day'
      | 'itinerary_fill_day_limit'
      | 'itinerary_fill_day_auto_limit';
    plan?: string;
    error?: string;
    errorCode?: string;
  };
  ai_request_started: {
    flow: 'trip_planning' | 'activity_suggestions' | 'trip_companion';
    endpoint: string;
    intent?: string;
    tier?: 'free' | 'premium' | 'unknown';
  };
  ai_request_completed: {
    flow: 'trip_planning' | 'activity_suggestions' | 'trip_companion';
    endpoint: string;
    latencyMs: number;
    resultCount?: number;
    tier?: 'free' | 'premium' | 'unknown';
  };
  ai_request_failed: {
    flow: 'trip_planning' | 'activity_suggestions' | 'trip_companion';
    endpoint: string;
    code?: string;
    message?: string;
    tier?: 'free' | 'premium' | 'unknown';
  };
  ai_fallback_used: {
    flow: 'trip_planning' | 'activity_suggestions' | 'trip_companion';
    endpoint: string;
    reason?: string;
  };
  ai_result_accepted: {
    flow: 'trip_planning' | 'activity_suggestions' | 'trip_companion';
    endpoint: string;
    resultType?: string;
    resultId?: string;
  };
  ai_feedback_submitted: {
    flow: 'trip_planning' | 'activity_suggestions' | 'trip_companion';
    endpoint: string;
    value: 'helpful' | 'not_helpful';
    messageId?: string;
  };
  rating_prompt_shown: {
    trigger: RatingPromptTrigger;
    source?: string;
    tripId?: string;
    bookingsCount?: number;
  };
  rating_prompt_dismissed: {
    trigger: RatingPromptTrigger;
    action: 'not_now' | 'dismissed';
  };
  rating_prompt_opted_out: {
    trigger: RatingPromptTrigger;
  };
  rating_prompt_attempted: {
    trigger: RatingPromptTrigger;
    path: 'native' | 'fallback';
  };
  rating_prompt_fallback_opened: {
    trigger: RatingPromptTrigger;
    platform: 'ios' | 'android';
    opened: boolean;
  };
  notification_value_prompt_shown: {
    moment: 'template_created' | 'email_sync_success' | 'trip_completed';
    source: string;
  };
  notification_value_prompt_granted: {
    moment: 'template_created' | 'email_sync_success' | 'trip_completed';
    source: string;
  };
  notification_value_prompt_denied: {
    moment: 'template_created' | 'email_sync_success' | 'trip_completed';
    source: string;
    reason: 'user_cancelled' | 'os_denied';
  };
  itinerary_save_timeout: {
    tripId: string;
    category: string;
    isEdit: boolean;
    outcome: 'resolved_during_grace' | 'still_pending' | 'failed_after_timeout';
  };
  sanitization_intercepted: {
    scope: 'travel_news' | 'weather_forecast' | 'places_cache';
    reason:
      | 'missing_country'
      | 'empty_city'
      | 'invalid_country_code'
      | 'undefined_fields_removed';
    count: number;
    droppedFields?: string[];
    removedCount?: number;
  };
  trip_cost_confidence_viewed: {
    tripId: string;
    confidence: string;
    rangeMin: number;
    rangeMax: number;
  };
  trip_cost_confidence_adjusted: {
    tripId: string;
    style: string;
    bufferPct: number;
  };
  trip_cost_confidence_budget_linked: {
    tripId: string;
  };
  compare_options_opened: {
    tripId: string;
    optionCount: number;
    context?: string;
  };
  compare_weights_changed: {
    tripId: string;
    weights: Record<string, number>;
  };
  compare_option_applied: {
    tripId: string;
    optionId: string;
    recommended: boolean;
  };
  feasibility_warning_shown: {
    tripId: string;
    severity: string;
    ruleId: string;
  };
  feasibility_warning_resolved: {
    tripId: string;
    ruleId: string;
    resolution: string;
  };
  feasibility_warning_accepted_risk: {
    tripId: string;
    ruleId: string;
  };
  mobility_row_viewed: {
    tripId: string;
    itemId: string;
    recommendedMode?: string;
  };
  mobility_sheet_opened: {
    tripId: string;
    itemId?: string;
    surface: 'plan' | 'maps';
  };
  mobility_navigation_started: {
    tripId: string;
    itemId: string;
    mode: string;
    surface: 'plan' | 'maps';
  };
};

export type AnalyticsEventName = keyof AnalyticsEventPayloadMap;

export type AnalyticsEnvelope<K extends AnalyticsEventName> = {
  event: K;
  payload: AnalyticsEventPayloadMap[K];
  userId?: string;
  createdAt: Date;
};

const buildEnvelope = <K extends AnalyticsEventName>(
  event: K,
  payload: AnalyticsEventPayloadMap[K],
  userId?: string,
): AnalyticsEnvelope<K> => ({
  event,
  payload,
  userId,
  createdAt: new Date(),
});

/** Firestore rejects map fields set to undefined — strip them before write. */
const stripUndefinedPayload = <T extends Record<string, unknown>>(payload: T): Partial<T> => {
  const sanitized: Partial<T> = {};
  for (const key of Object.keys(payload) as (keyof T)[]) {
    if (payload[key] !== undefined) {
      sanitized[key] = payload[key];
    }
  }
  return sanitized;
};

export const logAnalyticsEvent = async <K extends AnalyticsEventName>(
  event: K,
  payload: AnalyticsEventPayloadMap[K],
): Promise<void> => {
  try {
    const { firebaseUser } = useUserStore.getState();
    const envelope = buildEnvelope(event, payload, firebaseUser?.uid);
    const doc: Record<string, unknown> = {
      event: envelope.event,
      payload: stripUndefinedPayload(envelope.payload as Record<string, unknown>),
      createdAt: serverTimestamp(),
    };
    if (envelope.userId) {
      doc.userId = envelope.userId;
    }
    await addDoc(collection(db, 'analyticsEvents'), doc);
  } catch (error) {
    console.warn('[analyticsLogger] Failed to log event', event, error);
  }
};

export { buildEnvelope as buildAnalyticsEnvelope };






